/**
 * Purchases — the software provider's money hub (Structure.md navigation):
 *
 *   Orders            hardware purchases: what they bought, when, shipment
 *                     tracking, and the devices registered from each order
 *   Recurring cost    the plan summary and monthly service statements
 *                     (billed on the deployed device count)
 *   Payment methods   card onboarding (Stripe), default card, invoices
 *
 * Tab selection is URL-driven (`?tab=`).
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { IconX } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { isCompanyAdmin, isSuperAdmin } from '../services/users';
import {
  getBillingSummary,
  getPaymentMethods,
  createCardSetupIntent,
  setDefaultPaymentMethod,
  removePaymentMethod,
  getInvoices,
  getServiceStatements,
  SavedPaymentMethod,
} from '../services/billing';
import { OrdersPanel } from './Store';
import { SegmentedTabs } from '../components/SegmentedTabs';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  SectionTitle,
  DetailCard,
  Button,
  LinkButton,
  HelperText,
  EmptyState,
  MutedText,
  ErrorAlert,
  StatusChip,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  H2Title,
} from '../components/styled';

type PurchasesTab = 'orders' | 'recurring' | 'payment';

const TABS: { id: PurchasesTab; label: string }[] = [
  { id: 'orders',    label: 'Orders' },
  { id: 'recurring', label: 'Recurring cost' },
  { id: 'payment',   label: 'Payment methods' },
];

function isPurchasesTab(v: string | null): v is PurchasesTab {
  return v === 'orders' || v === 'recurring' || v === 'payment';
}

const FormCard = styled(DetailCard)`
  max-width: 640px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const SummaryCell = styled(DetailCard)`
  padding: ${({ theme }) => theme.spacing(4)};
`;

const SummaryLabel = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.font.color.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const SummaryValue = styled.div`
  font-size: ${({ theme }) => theme.font.size.xl};
  font-weight: ${({ theme }) => theme.font.weight.semibold};
`;

const CardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3, 0)};
  border-bottom: 1px solid ${({ theme }) => theme.border.primary};

  &:last-of-type {
    border-bottom: none;
  }
`;

const CardBrand = styled.span`
  font-weight: ${({ theme }) => theme.font.weight.medium};
  text-transform: capitalize;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const StripeMount = styled.div`
  min-height: 120px;
`;

// ─── Stripe.js loader ────────────────────────────────────────────────────────

let stripeJsPromise: Promise<any> | null = null;

function loadStripeJs(): Promise<any> {
  if ((window as any).Stripe) return Promise.resolve((window as any).Stripe);
  if (!stripeJsPromise) {
    stripeJsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => {
        if ((window as any).Stripe) resolve((window as any).Stripe);
        else reject(new Error('Stripe.js loaded but window.Stripe is missing'));
      };
      script.onerror = () => {
        stripeJsPromise = null;
        reject(new Error('Failed to load Stripe.js'));
      };
      document.head.appendChild(script);
    });
  }
  return stripeJsPromise;
}

// ─── Add-card modal ──────────────────────────────────────────────────────────

function AddCardModal({ companyId, onClose, onSaved }: {
  companyId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const mountRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<any>(null);
  const elementsRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const intent = await createCardSetupIntent(companyId);
        if (!intent.publishableKey) {
          throw new Error('Stripe publishable key is not configured (STRIPE_PUBLISHABLE_KEY)');
        }
        const Stripe = await loadStripeJs();
        if (cancelled) return;
        const stripe = Stripe(intent.publishableKey);
        const elements = stripe.elements({ clientSecret: intent.clientSecret });
        const paymentElement = elements.create('payment');
        paymentElement.mount(mountRef.current!);
        paymentElement.on('ready', () => { if (!cancelled) setReady(true); });
        stripeRef.current = stripe;
        elementsRef.current = elements;
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Failed to start card setup');
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  async function handleSave() {
    if (!stripeRef.current || !elementsRef.current) return;
    setSaving(true);
    setError('');
    try {
      const result = await stripeRef.current.confirmSetup({
        elements: elementsRef.current,
        redirect: 'if_required',
      });
      if (result.error) {
        setError(result.error.message || 'Card could not be saved');
      } else {
        onSaved();
      }
    } catch (err: any) {
      setError(err.message || 'Card could not be saved');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <H2Title>Add payment method</H2Title>
          <ModalCloseButton onClick={onClose} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          {error && <ErrorAlert>{error}</ErrorAlert>}
          <StripeMount ref={mountRef} />
          {!ready && !error && <MutedText>Loading secure card form…</MutedText>}
          <HelperText>
            Card details go directly to Stripe — they never touch Simkura servers.
          </HelperText>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={handleSave} disabled={!ready || saving}>
            {saving ? 'Saving…' : 'Save card'}
          </Button>
        </ModalFooter>
      </ModalCard>
    </ModalOverlay>
  );
}

// ─── Shared helpers ──────────────────────────────────────────────────────────

const money = (minor: number, cur: string) => `${(minor / 100).toFixed(2)} ${(cur || 'usd').toUpperCase()}`;

// ─── Recurring cost tab ──────────────────────────────────────────────────────

function RecurringPanel({ companyId }: { companyId: string }) {
  const { data: summary } = useQuery({
    queryKey: ['system-billing-summary', companyId],
    queryFn: () => getBillingSummary(companyId as any) as Promise<any>,
  });
  const { data: statementData } = useQuery({
    queryKey: ['system-service-statements', companyId],
    queryFn: () => getServiceStatements(companyId),
  });
  const statements = statementData?.statements ?? [];
  const statementVariant = (s: string) => (s === 'paid' ? 'green' : s === 'invoiced' ? 'warning' : 'gray');

  const pricePerDevice = Number(summary?.billing?.pricePerDevice ?? 0);
  const deployed = Number(summary?.devices?.deployed ?? 0);
  const currency = (summary?.billing?.currency || 'usd').toUpperCase();

  return (
    <>
      <SummaryGrid>
        <SummaryCell>
          <SummaryLabel>Deployed devices</SummaryLabel>
          <SummaryValue>{deployed}</SummaryValue>
        </SummaryCell>
        <SummaryCell>
          <SummaryLabel>Billing method</SummaryLabel>
          <SummaryValue style={{ textTransform: 'capitalize' }}>{summary?.billing?.method || '—'}</SummaryValue>
        </SummaryCell>
        <SummaryCell>
          <SummaryLabel>Legacy price / device</SummaryLabel>
          <SummaryValue>{pricePerDevice.toFixed(2)} {currency}</SummaryValue>
        </SummaryCell>
      </SummaryGrid>

      <FormCard>
        <SectionTitle>Service statements</SectionTitle>
        <HelperText>
          Monthly service billing for your deployed marketplace devices — each
          device at the rate from its order. Deployment turns on at a device's
          first contact and drives this count.
        </HelperText>
        {statements.length === 0 ? (
          <EmptyState><p>No service statements yet.</p></EmptyState>
        ) : (
          statements.map((s) => (
            <CardRow key={s.id}>
              <div>
                {new Date(s.period_start).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}
                <MutedText>
                  &nbsp;· {s.lines.reduce((n, l) => n + l.device_count, 0)} device{s.lines.reduce((n, l) => n + l.device_count, 0) === 1 ? '' : 's'}
                  &nbsp;· {money(s.total_cents, s.currency)}
                </MutedText>
                {s.status === 'invoiced' && s.charge_failure_reason && (
                  <div><MutedText>unpaid — {s.charge_failure_reason.replace(/_/g, ' ')}</MutedText></div>
                )}
              </div>
              <CardActions>
                <StatusChip variant={statementVariant(s.status)}>{s.status}</StatusChip>
              </CardActions>
            </CardRow>
          ))
        )}
      </FormCard>
    </>
  );
}

// ─── Payment methods tab ─────────────────────────────────────────────────────

function PaymentPanel({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const [showAddCard, setShowAddCard] = useState(false);

  const { data: pm, isLoading: pmLoading } = useQuery({
    queryKey: ['system-payment-methods', companyId],
    queryFn: () => getPaymentMethods(companyId),
  });
  const { data: invoiceData } = useQuery({
    queryKey: ['system-invoices', companyId],
    queryFn: () => getInvoices(companyId),
  });

  const refreshMethods = () =>
    queryClient.invalidateQueries({ queryKey: ['system-payment-methods', companyId] } as any);

  const defaultMutation = useMutation({
    mutationFn: (paymentMethodId: string) => setDefaultPaymentMethod(companyId, paymentMethodId),
    onSuccess: refreshMethods,
  });
  const removeMutation = useMutation({
    mutationFn: (paymentMethodId: string) => removePaymentMethod(companyId, paymentMethodId),
    onSuccess: refreshMethods,
  });

  const methods: SavedPaymentMethod[] = pm?.methods || [];
  const invoiceVariant = (status: string) =>
    status === 'paid' ? 'green' : status === 'open' ? 'warning' : 'gray';

  return (
    <>
      {pm?.apiAccess?.gated && (
        <ErrorAlert as="div">
          <strong>API access is inactive.</strong> Your API keys return
          {' '}<code>403 payment_required</code> until a payment method is added below.
        </ErrorAlert>
      )}

      <FormCard>
        <SectionTitle>Payment methods</SectionTitle>
        {(defaultMutation.isError || removeMutation.isError) && (
          <ErrorAlert>
            {((defaultMutation.error || removeMutation.error) as any)?.response?.data?.error
              || 'Payment method update failed'}
          </ErrorAlert>
        )}

        {pmLoading && <MutedText>Loading payment methods…</MutedText>}

        {pm && !pm.configured && (
          <EmptyState>
            <p>Card payments aren't enabled on this environment yet.</p>
            <MutedText>
              Once Stripe is configured, you'll add and manage your organization's
              cards here.
            </MutedText>
          </EmptyState>
        )}

        {pm?.configured && (
          <>
            {methods.length === 0 ? (
              <EmptyState>
                <p>No payment method on file.</p>
                <MutedText>Add a card so your subscription and hardware purchases can be billed.</MutedText>
              </EmptyState>
            ) : (
              methods.map((m) => (
                <CardRow key={m.id}>
                  <div>
                    <CardBrand>{m.brand || 'card'}</CardBrand>
                    {' '}•••• {m.last4}
                    <MutedText>
                      &nbsp;· expires {String(m.expMonth).padStart(2, '0')}/{m.expYear}
                    </MutedText>
                  </div>
                  <CardActions>
                    {pm.defaultPaymentMethodId === m.id ? (
                      <StatusChip variant="green">Default</StatusChip>
                    ) : (
                      <LinkButton
                        onClick={() => defaultMutation.mutate(m.id)}
                        disabled={defaultMutation.isPending}
                      >
                        Make default
                      </LinkButton>
                    )}
                    <LinkButton
                      onClick={() => {
                        if (window.confirm(`Remove ${m.brand || 'card'} ending ${m.last4}?`)) {
                          removeMutation.mutate(m.id);
                        }
                      }}
                      disabled={removeMutation.isPending}
                    >
                      Remove
                    </LinkButton>
                  </CardActions>
                </CardRow>
              ))
            )}
            <div style={{ marginTop: 16 }}>
              <Button type="button" variant="secondary" onClick={() => setShowAddCard(true)}>
                Add card
              </Button>
            </div>
          </>
        )}
      </FormCard>

      <FormCard style={{ marginTop: 24 }}>
        <SectionTitle>Invoices</SectionTitle>
        {invoiceData && !invoiceData.configured && (
          <MutedText>Invoice history appears here once payments are enabled.</MutedText>
        )}
        {invoiceData?.configured && invoiceData.invoices.length === 0 && (
          <EmptyState><p>No invoices yet.</p></EmptyState>
        )}
        {invoiceData?.upcoming && (
          <HelperText>
            Upcoming: {money(invoiceData.upcoming.amountDue, invoiceData.upcoming.currency)}
            {invoiceData.upcoming.periodEnd && ` · bills ${new Date(invoiceData.upcoming.periodEnd).toLocaleDateString()}`}
          </HelperText>
        )}
        {(invoiceData?.invoices || []).map((inv) => (
          <CardRow key={inv.id}>
            <div>
              {inv.created ? new Date(inv.created).toLocaleDateString() : '—'}
              {inv.number && <MutedText>&nbsp;· {inv.number}</MutedText>}
              <MutedText>&nbsp;· {money(inv.total, inv.currency)}</MutedText>
            </div>
            <CardActions>
              <StatusChip variant={invoiceVariant(inv.status)}>{inv.status}</StatusChip>
              {inv.hostedInvoiceUrl && (
                <a href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer">View</a>
              )}
            </CardActions>
          </CardRow>
        ))}
      </FormCard>

      {showAddCard && (
        <AddCardModal
          companyId={companyId}
          onClose={() => setShowAddCard(false)}
          onSaved={() => { setShowAddCard(false); refreshMethods(); }}
        />
      )}
    </>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function Purchases() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab: PurchasesTab = isPurchasesTab(tabParam) ? tabParam : 'orders';
  const setTab = (id: PurchasesTab) => setSearchParams(id === 'orders' ? {} : { tab: id });

  const companyId = user?.company_id ? String(user.company_id) : '';

  if (!isCompanyAdmin(user) || isSuperAdmin(user) || !companyId) {
    return (
      <PageContainer>
        <PageHeader><PageTitle>Purchases</PageTitle></PageHeader>
        <EmptyState>
          <p>Purchases is the company admin's hub for orders, recurring cost and payment.</p>
        </EmptyState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Purchases</PageTitle>
      </PageHeader>
      <SegmentedTabs tabs={TABS} activeId={activeTab} onChange={(id) => setTab(id as PurchasesTab)} />
      {activeTab === 'orders' && <OrdersPanel />}
      {activeTab === 'recurring' && <RecurringPanel companyId={companyId} />}
      {activeTab === 'payment' && <PaymentPanel companyId={companyId} />}
    </PageContainer>
  );
}

export default Purchases;
